import React, { useEffect, useState, useRef } from "react";
import { db } from "../../firebase";
import {
  doc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  getDoc,
  runTransaction,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import GameChat from "../lobby/GameChat";

function getWinner(board) {
  const lines = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [0, 3, 6],
    [1, 4, 7],
    [2, 5, 8],
    [0, 4, 8],
    [2, 4, 6],
  ];
  for (const [a, b, c] of lines) {
    if (board[a] && board[a] === board[b] && board[a] === board[c])
      return board[a];
  }
  return null;
}

export default function XOGameRoom({
  gameId,
  user,
  setCurrentGameId,
  handleRematch,
  onlineUsers,
  handleInvite,
}) {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const payoutDone = useRef(false);
  const navigate = useNavigate();
  const [payoutShown, setPayoutShown] = useState(false);

  useEffect(() => {
    if (!gameId) return;
    const unsub = onSnapshot(doc(db, "games", gameId), (snap) => {
      setGame(snap.exists() ? snap.data() : null);
      setLoading(false);
    });
    return unsub;
  }, [gameId]);

  useEffect(() => {
    if (!game || game.status !== "active") return;

    const isBotGame =
      game.playerX?.userId === "bot-ai-001" ||
      game.playerO?.userId === "bot-ai-001";
    const botUid = "bot-ai-001";
    const isBotTurn =
      (game.turn === "X" && game.playerX?.userId === botUid) ||
      (game.turn === "O" && game.playerO?.userId === botUid);

    // -- If it's the bot's turn, make sure bot moves after a delay, even if user is idle
    let botMoveTimeout = null;
    if (isBotGame && isBotTurn && !game.winner) {
      // Only allow bot to play if not already finished
      botMoveTimeout = setTimeout(async () => {
        // Double-check game still active and bot's turn
        const latestSnap = await getDoc(doc(db, "games", gameId));
        const latestGame = latestSnap.data();
        if (
          latestGame &&
          latestGame.status === "active" &&
          ((latestGame.turn === "X" && latestGame.playerX?.userId === botUid) ||
            (latestGame.turn === "O" &&
              latestGame.playerO?.userId === botUid)) &&
          !latestGame.winner
        ) {
          // Calculate bot move
          const bestMove = getBestMove(latestGame.board, latestGame.turn);
          // Make the move (triggers updateLastActive for bot)
          if (bestMove !== null) {
            // Update bot lastActive as well
            await updateDoc(doc(db, "games", gameId), {
              [`lastActive.${botUid}`]: Date.now(),
            });
            await handleMove(bestMove);
          }
        }
      }, Math.random() * 1500 + 1200); // Random 1.2s–2.7s

      return () => clearTimeout(botMoveTimeout);
    }
  }, [game, gameId]);

  useEffect(() => {
    if (!game || !game.status || game.paidOut || !game.winner || payoutShown)
      return;

    const processPayout = async () => {
      const gameRef = doc(db, "games", gameId);

      try {
        await runTransaction(db, async (transaction) => {
          const freshSnap = await transaction.get(gameRef);
          const freshData = freshSnap.data();
          if (!freshData || freshData.paidOut) return;

          const bet = Number(freshData.betAmount || 0.5);
          const prize = +(bet * 2 * 0.9).toFixed(2);
          const userRef = doc(db, "users", user.uid);
          const userSnap = await transaction.get(userRef);
          const currentBalance = userSnap.data()?.balance || 0;

          let updatedBalance = currentBalance;

          if (freshData.winner === "draw") {
            // 🧠 Only let Player X handle draw payout
            if (freshData.playerX?.userId === user.uid) {
              updatedBalance += bet;
              transaction.update(userRef, {
                balance: +updatedBalance.toFixed(2),
              });
              transaction.update(gameRef, { paidOut: true });
              toast.success(`It's a draw! Your ${bet} USDT is refunded.`);
            }
          } else if (freshData.winner === user.uid) {
            // ✅ Winner gets 90% of pool
            updatedBalance += prize;
            transaction.update(userRef, {
              balance: +updatedBalance.toFixed(2),
            });
            transaction.update(gameRef, { paidOut: true });
            toast.success(`You win! +${prize} USDT credited.`);
          }
        });

        setPayoutShown(true);
      } catch (err) {
        console.error("❌ Payout error:", err.message);
      }
    };

    processPayout();
  }, [game, user.uid, payoutShown]);

  // Update my lastActive while game active
  useEffect(() => {
    if (!game || game.status !== "active" || !user?.uid) return;
    const interval = setInterval(() => {
      updateLastActive(gameId, user.uid);
    }, 10000);
    return () => clearInterval(interval);
  }, [game, gameId, user?.uid]);

  // Forfeit: only if it’s opponent’s turn and they are inactive
  useEffect(() => {
    if (!game || !user?.uid || game.status !== "active") return;

    const myUid = user.uid;
    const oppUid = game.players.find((uid) => uid !== myUid);

    const isBotGame =
      game.playerX?.userId === "bot-ai-001" ||
      game.playerO?.userId === "bot-ai-001";
    if (isBotGame) return; // ⬅️ Only allow timeouts for real vs real

    // Is it opponent's turn? If yes, and they are inactive, I should win
    const isOpponentsTurn =
      (game.turn === "X" && game.playerX?.userId !== myUid) ||
      (game.turn === "O" && game.playerO?.userId !== myUid);

    if (isOpponentsTurn) {
      const oppLastActive = game.lastActive?.[oppUid] || 0;
      const now = Date.now();

      // If opponent inactive >60s, and not finished, forfeit
      if (
        now - oppLastActive > 60000 &&
        game.status === "active" &&
        !game.winner
      ) {
        handleWinByForfeit(gameId, myUid);
      }
    }
  }, [game, user, gameId]);

  useEffect(() => {
    if (!game || !game.status || !game.winner) return;
    // console.log("game", game.winner, user.uid);
    // Play only once
    if (game.status === "finished" && !payoutShown) {
      if (game.winner === user.uid) {
        playSound("/sounds/win.mp3");
      } else if (game.winner === "draw") {
        playSound("/sounds/draw.mp3");
      } else {
        playSound("/sounds/lose.mp3");
      }
      setPayoutShown(true);
    }
  }, [game, user.uid, payoutShown]);

  // When playerO joins, set their lastActive too
  useEffect(() => {
    if (
      game &&
      game.status === "waiting" &&
      game.playerX?.userId !== user.uid &&
      !game.playerO
    ) {
      updateDoc(doc(db, "games", gameId), {
        playerO: {
          userId: user.uid,
          username: user.displayName || "Player",
          avatar: user.photoURL || "",
        },
        status: "active",
        updatedAt: serverTimestamp(),
        [`lastActive.${user.uid}`]: Date.now(),
      });
    }
  }, [game, user, gameId]);

  useEffect(() => {
    if (!game || game.status !== "finished" || game.statsUpdated) return;

    // Only update once per game
    async function updatePlayerStats() {
      const { playerX, playerO, winner, betAmount } = game;
      if (!playerX?.userId || !playerO?.userId) return;
      const bet = Number(betAmount || 0.5);

      // IQ formula:
      // Win: +10 IQ, Draw: +3 IQ, Lose: 0 IQ
      let x_iq = 0,
        o_iq = 0;
      let x_win = 0,
        o_win = 0;
      let x_cash = 0,
        o_cash = 0;

      if (winner === "draw") {
        x_iq = o_iq = 3;
        x_cash = o_cash = 0; // refund, no win
      } else if (winner === playerX?.userId) {
        x_iq = 10;
        o_iq = 0;
        x_win = 1;
        o_win = 0;
        x_cash = bet * 0.8; // winner gets 80% of pool (you can set this)
        o_cash = -bet;
      } else if (winner === playerO?.userId) {
        o_iq = 10;
        x_iq = 0;
        o_win = 1;
        x_win = 0;
        o_cash = bet * 0.8;
        x_cash = -bet;
      }

      // Update playerX
      await updateUserStats(playerX?.userId, {
        iqField: "xo_iq",
        winField: "xo_wins",
        cashField: "xo_cash",
        iq: x_iq,
        win: x_win,
        cash: x_cash,
      });

      // Update playerO
      await updateUserStats(playerO?.userId, {
        iqField: "xo_iq",
        winField: "xo_wins",
        cashField: "xo_cash",
        iq: o_iq,
        win: o_win,
        cash: o_cash,
      });

      // Mark game as updated to avoid double stats
      await updateDoc(doc(db, "games", gameId), {
        statsUpdated: true,
      });
    }

    updatePlayerStats();
  }, [game, gameId]);

  // --- Bot player: simulate real moves ---
  useEffect(() => {
    if (!game || game.status !== "active") return;

    const isBotGame =
      game.playerX?.userId === "bot-ai-001" ||
      game.playerO?.userId === "bot-ai-001";
    const botTurn =
      (game.turn === "X" && game.playerX?.userId === "bot-ai-001") ||
      (game.turn === "O" && game.playerO?.userId === "bot-ai-001");

    console.log("Checking for bot move", {
      isBotGame,
      botTurn,
      board: game.board,
    });

    if (isBotGame && botTurn) {
      const timeout = setTimeout(() => {
        const bestMove = getBestMove(game.board, game.turn);
        console.log("Bot decided move:", bestMove);
        if (bestMove !== null) {
          handleMove(bestMove);
        }
      }, Math.random() * 2000 + 1000);

      return () => clearTimeout(timeout);
    }
  }, [game]); // ✅ must trigger every board update

  async function updateUserStats(
    uid,
    { iqField, winField, cashField, iq, win, cash }
  ) {
    const userRef = doc(db, "users", uid);
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(userRef);
      if (!snap.exists()) return; // ✅ fix for "document doesn't exist"

      const data = snap.data() || {};
      const nextIQ = +(Number(data[iqField] || 0) + iq).toFixed(2);
      const nextWins = (data[winField] || 0) + win;
      const nextCash = +(Number(data[cashField] || 0) + cash).toFixed(2);

      transaction.update(userRef, {
        [iqField]: nextIQ,
        [winField]: nextWins,
        [cashField]: nextCash,
        overall_iq: +(Number(data.overall_iq || 0) + iq).toFixed(2),
        overall_wins: (data.overall_wins || 0) + win,
        overall_cash: +(Number(data.overall_cash || 0) + cash).toFixed(2),
      });
    });
  }

  async function handleWinByForfeit(gameId, winnerUid) {
    const gameRef = doc(db, "games", gameId);

    // Only process if not already finished
    const snap = await getDoc(gameRef);
    if (!snap.exists()) return;
    const gameData = snap.data();
    if (gameData.status !== "active" || !!gameData.winner) return;

    await updateDoc(gameRef, {
      status: "finished",
      winner: winnerUid,
      winReason: "opponent_timeout",
      endedAt: Date.now(),
    });
  }

  async function updateLastActive(gameId, userId) {
    const gameRef = doc(db, "games", gameId);
    await updateDoc(gameRef, {
      [`lastActive.${userId}`]: Date.now(),
    });
  }

  function playSound(url) {
    const audio = new window.Audio(url);
    audio.volume = 0.55;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  const handleMove = async (idx) => {
    if (!game) return;
    const { board, turn, playerX, playerO, status, winner } = game;
    if (status !== "active" || winner) return;

    const currentUser = turn === "X" ? playerX?.userId : playerO?.userId;

    // ✅ Let human or bot make the move
    if (user.uid !== currentUser && currentUser !== "bot-ai-001") return;

    if (board[idx]) return;

    playSound("/sounds/xo-click.mp3");

    const newBoard = board.slice();
    newBoard[idx] = turn;
    const win = getWinner(newBoard);

    let nextStatus = status;
    let nextWinner = null;
    let nextTurn = turn === "X" ? "O" : "X";
    if (win) {
      nextStatus = "finished";
      nextWinner = win === "X" ? playerX?.userId : playerO?.userId;
    } else if (newBoard.every(Boolean)) {
      nextStatus = "finished";
      nextWinner = "draw";
    }

    await updateDoc(doc(db, "games", gameId), {
      board: newBoard,
      turn: nextTurn,
      status: nextStatus,
      winner: nextWinner,
      updatedAt: serverTimestamp(),
    });
  };

  if (loading) return <div className="text-center mt-24">Loading game...</div>;
  if (!game) return <div className="text-center mt-24">Game not found.</div>;

  const youAre = game?.playerX?.userId === user.uid ? "X" : "O";
  const oppName =
    youAre === "X"
      ? game.playerO?.username || "Waiting..."
      : game.playerX?.username;

  const isUserWinner = game.winner === user.uid;
  const isDraw = game.winner === "draw";

  function shortName(name) {
    if (!name) return "";
    if (window.innerWidth < 500) {
      const [first, last] = name.split(" ");
      if (last) {
        return first + " " + last[0] + "…";
      }
      return first.length > 5 ? first.slice(0, 5) + "…" : first;
    }
    return name;
  }

  function getBestMove(board, player) {
    const opponent = player === "X" ? "O" : "X";

    const isWinner = (brd, p) => {
      const lines = [
        [0, 1, 2],
        [3, 4, 5],
        [6, 7, 8],
        [0, 3, 6],
        [1, 4, 7],
        [2, 5, 8],
        [0, 4, 8],
        [2, 4, 6],
      ];
      return lines.some(
        ([a, b, c]) => brd[a] === p && brd[b] === p && brd[c] === p
      );
    };

    const minimax = (brd, isMax) => {
      if (isWinner(brd, player)) return { score: 1 };
      if (isWinner(brd, opponent)) return { score: -1 };
      if (brd.every(Boolean)) return { score: 0 };

      const moves = [];
      brd.forEach((cell, i) => {
        if (!cell) {
          const newBoard = [...brd];
          newBoard[i] = isMax ? player : opponent;
          const result = minimax(newBoard, !isMax);
          moves.push({ index: i, score: result.score });
        }
      });

      return isMax
        ? moves.reduce((a, b) => (a.score > b.score ? a : b))
        : moves.reduce((a, b) => (a.score < b.score ? a : b));
    };

    const result = minimax(board, true);
    return result?.index ?? null;
  }

  return (
    <div className="mt-24 flex flex-col items-center gap-5 px-4">
      <div className="flex items-center justify-center gap-4 mb-3 flex-wrap">
        <motion.div
          initial={{ scale: 0.9, opacity: 0.7 }}
          animate={{
            scale: game.turn === "X" ? 1.08 : 1,
            opacity: 1,
            boxShadow: game.turn === "X" ? "0 4px 24px 2px #a3e63566" : "none",
          }}
          transition={{ type: "spring", stiffness: 300 }}
          className={`flex items-center gap-2 px-5 py-3 rounded-full shadow text-lg font-bold
      ${
        game.turn === "X"
          ? "bg-lime-500 text-white"
          : "bg-slate-800 text-lime-300 opacity-70"
      }
      max-w-[155px] sm:max-w-[200px] truncate`}
          style={{
            minWidth: 80,
            justifyContent: "center",
          }}
        >
          <span
            className="truncate text-base sm:text-lg font-bold tracking-tight"
            title={game.playerX?.username || "Player X"}
          >
            {shortName(game.playerX?.username || "Player X")}
          </span>
          <span className="text-2xl font-black ml-2">X</span>
        </motion.div>

        <motion.div
          initial={{ scale: 0.9, opacity: 0.7 }}
          animate={{
            scale: game.turn === "O" ? 1.08 : 1,
            opacity: 1,
            boxShadow: game.turn === "O" ? "0 4px 24px 2px #fde04766" : "none",
          }}
          transition={{ type: "spring", stiffness: 300 }}
          className={`flex items-center gap-2 px-5 py-3 rounded-full shadow text-lg font-bold
      ${
        game.turn === "O"
          ? "bg-yellow-400 text-black"
          : "bg-slate-800 text-yellow-300 opacity-70"
      }
      max-w-[155px] sm:max-w-[200px] truncate`}
          style={{
            minWidth: 80,
            justifyContent: "center",
          }}
        >
          <span
            className="truncate text-base sm:text-lg font-bold tracking-tight"
            title={game.playerO?.username || "Player O"}
          >
            {shortName(game.playerO?.username || "Player O")}
          </span>
          <span className="text-2xl font-black ml-2">O</span>
        </motion.div>
      </div>
      <motion.div
        initial={{ scale: 0.97, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", delay: 0.1, stiffness: 120 }}
        className="grid aspect-square w-full max-w-[330px] grid-cols-3 gap-2 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-lime-900 px-3 pb-4 pt-3 shadow-xl sm:gap-3 sm:px-5 sm:pb-10 sm:pt-5"
      >
        {game.board.map((cell, i) => {
          const rounded =
            i === 0
              ? "rounded-tl-3xl"
              : i === 2
              ? "rounded-tr-3xl"
              : i === 6
              ? "rounded-bl-3xl"
              : i === 8
              ? "rounded-br-3xl"
              : "rounded-none";
          return (
            <button
              key={i}
              onClick={() => handleMove(i)}
              className={`aspect-square w-full text-4xl font-black shadow transition-all duration-200 sm:text-5xl
                ${rounded}
                ${
                  cell === "X"
                    ? "text-lime-400"
                    : cell === "O"
                    ? "text-yellow-400"
                    : "text-slate-300 hover:bg-slate-700/60"
                }
                ${
                  game.winner && [0, 4, 8, 2, 4, 6].includes(i)
                    ? "animate-pulse"
                    : ""
                }
                bg-[rgba(36,42,55,0.88)]
              `}
              disabled={!!cell || !!game.winner || game.turn !== youAre}
              style={{ outline: "none" }}
            >
              {cell}
            </button>
          );
        })}
      </motion.div>
      {/* Status text */}
      <div className="flex flex-col items-center gap-2 min-h-[42px]">
        {game.status === "waiting" && (
          <div className="text-xl font-bold text-yellow-300 animate-pulse">
            Waiting for opponent to join...
          </div>
        )}
        {game.status === "active" && (
          <div className="text-lg">
            {game.turn === youAre ? (
              <span className="text-lime-400 font-semibold">Your turn!</span>
            ) : (
              <span className="text-slate-400">{oppName}'s turn</span>
            )}
          </div>
        )}
        {game.status === "finished" && (
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className={`text-2xl font-black drop-shadow ${
              isUserWinner
                ? "text-lime-400"
                : isDraw
                ? "text-yellow-300"
                : "text-pink-400"
            }`}
          >
            {isUserWinner
              ? "You win! 🎉"
              : isDraw
              ? "It's a draw!"
              : "You lose!"}
          </motion.div>
        )}
      </div>
      {game.status === "active" && (
        <GameChat
          gameId={gameId}
          user={user}
          otherPlayer={
            game.playerX?.userId === user.uid ? game.playerO : game.playerX
          }
        />
      )}
      {/* Rematch/Back buttons */}
      {game.status === "finished" && (
        <div className="flex gap-4 mt-4">
          <button
            className="px-6 py-2 rounded-full bg-lime-500 hover:bg-lime-600 text-white font-semibold shadow"
            onClick={() => {
              if (typeof setCurrentGameId === "function")
                setCurrentGameId(null);
              navigate("/xogame");
            }}
          >
            Go Back to Lobby
          </button>
        </div>
      )}
    </div>
  );
}
